// src/components/WalletConnect.js
// ChainPlay.bet — Wallet layer (final)
// - Readonly RPC for reads + rotation on failure
// - Hardened connect path (switch network + rebuild provider/signer)
// - EIP-6963 multi-injected (MetaMask / Coinbase / etc.)
// - Modal with explicit wallet choice + mobile deep links
// - Preflight tx (estimateGas + call) + global tx lock to prevent duplicates
// - Remember last wallet
// - A11y (focus return, ARIA live), in-app browser hint
// - Analytics hooks via window.dataLayer (if present)

import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Button,
  CircularProgress,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Divider,
  Chip,
  useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { motion } from 'framer-motion';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import ADDRESSES from '../addresses.json';
import ChainPlayTokenABI from '../abis/ChainPlayToken.json';
import theme from '../theme';

/* ------------------------------------------------------------------ */
/* Theme tokens                                                        */
/* ------------------------------------------------------------------ */
const GRAD_PRIMARY = 'var(--grad-primary)';
const COLOR_PRIMARY = 'var(--color-primary)';
const COLOR_BORDER  = 'var(--color-border)';
const COLOR_TEXT    = 'var(--color-text)';
const COLOR_SUBTEXT = 'var(--color-subText)';
const COLOR_FOCUS   = 'var(--color-focus)';
const RADIUS_BUTTON = 'var(--radius-button)';
const BUTTON_LABEL_PRIMARY = 'var(--button-label-primary, #FFFFFF)';

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */
const getEnv = (k, fallback = '') =>
  (typeof process !== 'undefined' && process?.env?.[k]) || fallback;

const parseChainId = (v) => {
  if (typeof v === 'bigint') return Number(v);
  if (typeof v === 'string') return v.startsWith('0x') ? parseInt(v, 16) : parseInt(v, 10);
  return Number(v);
};

const isMobileUA = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(ua);
};

const isInAppBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /(FBAN|FB_IAB|FBAV|Instagram|Twitter|X\/|Line|Wechat|Weixin)/i.test(ua);
};

const dappUrl = () => {
  try {
    const url = (typeof window !== 'undefined' && window.location)
      ? window.location.origin
      : 'https://chainplay.bet';
    return url;
  } catch {
    return 'https://chainplay.bet';
  }
};

// Deep links for opening webpage inside wallet apps on mobile
const deepLinkFor = (name) => {
  const encoded = encodeURIComponent(dappUrl());
  switch (name) {
    case 'metamask':
      return `https://metamask.app.link/dapp/${encoded.replace(/^https?:\/\//, '')}`;
    case 'coinbase':
      return `https://go.cb-w.com/dapp?cb_url=${encoded}`;
    default:
      return null;
  }
};

// Persist last chain id so consumers can pick a readonly RPC accordingly
const LAST_CHAIN_KEY = 'cp:lastChainId';
export const getSavedChainId = () => {
  try {
    const raw = (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(LAST_CHAIN_KEY)) || '';
    const n = parseChainId(raw);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_CHAIN.chainId;
  } catch {
    return DEFAULT_CHAIN.chainId;
  }
};
export const setSavedChainId = (id) => {
  try {
    const n = parseChainId(id);
    if (Number.isFinite(n) && n > 0 && typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(LAST_CHAIN_KEY, String(n));
    }
  } catch {}
};

// EIP-6963 provider registry (if wallets announce themselves)
const getEip6963Providers = () => {
  try {
    return (window.__eip6963providers || [])
      .map((x) => x.provider)
      .filter(Boolean);
  } catch { return []; }
};

const collectInjectedProviders = () => {
  if (typeof window === 'undefined') return [];
  const out = [];
  const announced = getEip6963Providers();
  if (Array.isArray(announced) && announced.length) out.push(...announced);

  const { ethereum } = window;
  if (ethereum) {
    if (Array.isArray(ethereum.providers) && ethereum.providers.length) out.push(...ethereum.providers);
    else out.push(ethereum);
  }
  // Deduplicate by reference
  return out.filter((p, i, arr) => arr.indexOf(p) === i);
};

// Friendly label + id
const labelForProvider = (p) => {
  if (!p) return 'Browser Wallet';
  if (p.isMetaMask) return 'MetaMask';
  if (p.isCoinbaseWallet) return 'Coinbase Wallet';
  if (p.isRabby) return 'Rabby';
  if (p.isBraveWallet) return 'Brave Wallet';
  return 'Browser Wallet';
};
const idForProvider = (p) => {
  if (!p) return 'browser';
  if (p.isMetaMask) return 'metamask';
  if (p.isCoinbaseWallet) return 'coinbase';
  if (p.isRabby) return 'rabby';
  if (p.isBraveWallet) return 'brave';
  return 'browser';
};

/* ------------------------------------------------------------------ */
/* Chain Registry (Base live, others ready)                            */
/* ------------------------------------------------------------------ */
// Multi-prefix env resolver so CRA and Vite both work
const getEnvMany = (keys, fallback = '') => {
  for (const k of keys) {
    const v = getEnv(k);
    if (v) return v;
  }
  return fallback;
};

// Per-chain numeric env helpers (prefer chain-specific, then generic)
const envRpcForChainId = (id, fallback = '') => {
  const n = String(id);
  return (
    getEnvMany(
      [
        `REACT_APP_PUBLIC_RPC_URL_${n}`,
        `VITE_PUBLIC_RPC_URL_${n}`,
        `NEXT_PUBLIC_PUBLIC_RPC_URL_${n}`,
      ]
    ) ||
    getEnvMany(
      [
        'REACT_APP_PUBLIC_RPC_URL',
        'VITE_PUBLIC_RPC_URL',
        'VITE_RPC_URL',
        'NEXT_PUBLIC_RPC_URL',
      ],
      fallback
    )
  );
};

const envExplorerForChainId = (id, fallback = '') => {
  const n = String(id);
  return (
    getEnvMany(
      [
        `REACT_APP_BLOCK_EXPLORER_${n}`,
        `VITE_BLOCK_EXPLORER_${n}`,
        `NEXT_PUBLIC_BLOCK_EXPLORER_${n}`,
      ]
    ) || getEnv('REACT_APP_BLOCK_EXPLORER', fallback)
  );
};

// Central, extendable chain registry.
// comingSoon: set to false to enable switching/selection when contracts are deployed.
export const CHAIN_REGISTRY = {
  base: {
    key: 'base',
    chainId: 8453,
    chainIdHex: '0x2105',
    name: 'Base',
    shortName: 'Base',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: () => envRpcForChainId(8453, 'https://mainnet.base.org'),
    explorer: envExplorerForChainId(8453, 'https://basescan.org'),
    icon: 'base',
    comingSoon: false,
  },
  ethereum: {
    key: 'ethereum',
    chainId: 1,
    chainIdHex: '0x1',
    name: 'Ethereum',
    shortName: 'ETH',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: () => envRpcForChainId(1, 'https://cloudflare-eth.com'),
    explorer: envExplorerForChainId(1, 'https://etherscan.io'),
    icon: 'ethereum',
    comingSoon: true,
  },
  optimism: {
    key: 'optimism',
    chainId: 10,
    chainIdHex: '0xa',
    name: 'OP Mainnet',
    shortName: 'Optimism',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: () => envRpcForChainId(10, 'https://mainnet.optimism.io'),
    explorer: envExplorerForChainId(10, 'https://optimistic.etherscan.io'),
    icon: 'optimism',
    comingSoon: true,
  },
  polygon: {
    key: 'polygon',
    chainId: 137,
    chainIdHex: '0x89',
    name: 'Polygon',
    shortName: 'Polygon',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    rpcUrl: () => envRpcForChainId(137, 'https://polygon-rpc.com'),
    explorer: envExplorerForChainId(137, 'https://polygonscan.com'),
    icon: 'polygon',
    comingSoon: true,
  },
  arbitrum: {
    key: 'arbitrum',
    chainId: 42161,
    chainIdHex: '0xa4b1',
    name: 'Arbitrum One',
    shortName: 'Arbitrum',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: () => envRpcForChainId(42161, 'https://arb1.arbitrum.io/rpc'),
    explorer: envExplorerForChainId(42161, 'https://arbiscan.io'),
    icon: 'arbitrum',
    comingSoon: true,
  },
  avalanche: {
    key: 'avalanche',
    chainId: 43114,
    chainIdHex: '0xa86a',
    name: 'Avalanche C-Chain',
    shortName: 'Avalanche',
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    rpcUrl: () => envRpcForChainId(43114, 'https://api.avax.network/ext/bc/C/rpc'),
    explorer: envExplorerForChainId(43114, 'https://snowtrace.io'),
    icon: 'avalanche',
    comingSoon: true,
  },
  bsc: {
    key: 'bsc',
    chainId: 56,
    chainIdHex: '0x38',
    name: 'BNB Smart Chain',
    shortName: 'BSC',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrl: () => envRpcForChainId(56, 'https://bsc-dataseed.binance.org'),
    explorer: envExplorerForChainId(56, 'https://bscscan.com'),
    icon: 'bsc',
    comingSoon: true,
  },
  camp: {
    key: 'camp',
    chainId: 484,
    chainIdHex: '0x1e4',
    name: 'Camp Network',
    shortName: 'Camp',
    nativeCurrency: { name: 'CAMP', symbol: 'CAMP', decimals: 18 },
    rpcUrl: () => envRpcForChainId(484, 'https://rpc.camp.raas.gelato.cloud'),
    explorer: envExplorerForChainId(484, 'https://camp.cloud.blockscout.com'),
    icon: 'camp',
    comingSoon: true,
  },
};

export const DEFAULT_CHAIN_KEY = 'base';
export const DEFAULT_CHAIN = CHAIN_REGISTRY[DEFAULT_CHAIN_KEY];

export const getChainById = (cid) => {
  const n = Number(cid);
  return Object.values(CHAIN_REGISTRY).find((c) => c.chainId === n) || null;
};

export const getReadonlyRpcFor = (chainKeyOrId) => {
  if (typeof chainKeyOrId === 'string') {
    const c = CHAIN_REGISTRY[chainKeyOrId];
    return c?.rpcUrl?.() || DEFAULT_CHAIN.rpcUrl();
  }
  const c = getChainById(chainKeyOrId);
  return c?.rpcUrl?.() || DEFAULT_CHAIN.rpcUrl();
};

export const getExplorerFor = (chainKeyOrId) => {
  if (typeof chainKeyOrId === 'string') {
    const c = CHAIN_REGISTRY[chainKeyOrId];
    return c?.explorer || CHAIN_REGISTRY.base.explorer;
  }
  const c = getChainById(chainKeyOrId);
  return c?.explorer || CHAIN_REGISTRY.base.explorer;
};

// Request wallet to switch network; if not present, add it first.
export const switchToChain = async (chainKeyOrId) => {
  const cfg =
    typeof chainKeyOrId === 'string'
      ? CHAIN_REGISTRY[chainKeyOrId]
      : getChainById(chainKeyOrId);

  if (!cfg) throw new Error('Unknown chain');

  const params = [{ chainId: cfg.chainIdHex }];
  try {
    await (typeof window !== 'undefined'
      ? window.ethereum?.request?.({ method: 'wallet_switchEthereumChain', params })
      : Promise.resolve());
    return true;
  } catch (e) {
    if (e?.code === 4902) {
      try {
        await window.ethereum?.request?.({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: cfg.chainIdHex,
              chainName: cfg.name,
              nativeCurrency: cfg.nativeCurrency,
              rpcUrls: [cfg.rpcUrl()],
              blockExplorerUrls: [cfg.explorer],
            },
          ],
        });
        return true;
      } catch (err) {
        throw new Error(err?.message || `Add ${cfg.name} failed`);
      }
    }
    throw new Error(e?.message || 'Switch network failed');
  }
};

/* ------------------------------------------------------------------ */
/* End Chain Registry                                                  */
/* ------------------------------------------------------------------ */

// Preferred ordering for display and default selection
const sortProviders = (list) => {
  const score = (p) => (p?.isMetaMask ? 100 : p?.isCoinbaseWallet ? 90 : p?.isRabby ? 80 : 10);
  return [...list].sort((a, b) => score(b) - score(a));
};

const toHex = (n) => `0x${Number(n).toString(16)}`;

/* ------------------------------------------------------------------ */
/* Chain presets + RPC defaults                                        */
/* ------------------------------------------------------------------ */
const KNOWN_CHAINS = {
  8453: {
    chainId: '0x2105',
    chainName: 'Base',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.base.org'],
    blockExplorerUrls: ['https://basescan.org'],
  },
  484: {
    chainId: '0x1e4',
    chainName: 'Camp Network',
    nativeCurrency: { name: 'CAMP', symbol: 'CAMP', decimals: 18 },
    rpcUrls: ['https://rpc.camp.raas.gelato.cloud'],
    blockExplorerUrls: ['https://camp.cloud.blockscout.com'],
  },
};

const DEFAULT_RPC_FALLBACKS = {
  8453: ['https://mainnet.base.org'],
  484: ['https://rpc.camp.raas.gelato.cloud'],
};

const resolveChainConfig = (targetChainId, chainConfigProp) => {
  if (chainConfigProp?.chainId) return chainConfigProp;

  const envName = getEnv('REACT_APP_CHAIN_NAME') || getEnv('VITE_CHAIN_NAME');
  const envPublicRpc = (getEnv('REACT_APP_PUBLIC_RPC_URL') || getEnv('VITE_PUBLIC_RPC_URL') || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const envRpc = envPublicRpc.length
    ? envPublicRpc
    : (getEnv('REACT_APP_RPC_URL') || getEnv('VITE_RPC_URL') || '')
        .split(',').map(s => s.trim()).filter(Boolean);
  const envExplorer = (getEnv('REACT_APP_BLOCK_EXPLORER') || getEnv('VITE_BLOCK_EXPLORER') || '')
    .split(',').map(s => s.trim()).filter(Boolean);

  if (envRpc.length) {
    return {
      chainId: toHex(targetChainId),
      chainName: envName || `Chain ${targetChainId}`,
      nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
      rpcUrls: envRpc,
      blockExplorerUrls: envExplorer.length ? envExplorer : [],
    };
  }
  if (KNOWN_CHAINS[targetChainId]) return KNOWN_CHAINS[targetChainId];

  return {
    chainId: toHex(targetChainId),
    chainName: `EVM ${targetChainId}`,
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpcUrls: [],
    blockExplorerUrls: [],
  };
};

/* ------------------------------------------------------------------ */
/* Token helpers                                                       */
/* ------------------------------------------------------------------ */
const resolveTokenAddress = (chainId) => {
  const cid = String(chainId);
  const candidates = [
    getEnv(`REACT_APP_TOKEN_ADDRESS_${cid}`),
    getEnv(`VITE_TOKEN_ADDRESS_${cid}`),
    getEnv('REACT_APP_TOKEN_ADDRESS'),
    getEnv('VITE_TOKEN_ADDRESS'),
    getEnv(`REACT_APP_CPT_ADDRESS_${cid}`),
    getEnv(`VITE_CPT_ADDRESS_${cid}`),
    getEnv('REACT_APP_CPT_ADDRESS'),
    getEnv('VITE_CPT_ADDRESS'),
  ].filter(Boolean);

  if (candidates.length) return candidates[0];

  if (ADDRESSES && (ADDRESSES[cid] || ADDRESSES[String(cid)])) {
    const entry = ADDRESSES[cid] || ADDRESSES[String(cid)];
    if (entry && typeof entry.ChainPlayToken === 'string') return entry.ChainPlayToken;
  }
  return null;
};

const isBadDataDecode = (err) =>
  !!String(err?.message || '').toLowerCase().includes('could not decode result data');

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */
export const WalletContext = createContext();

/* ================================================================== */
/* Provider                                                            */
/* ================================================================== */
export const WalletProvider = ({
  children,
  autoConnect = false,
  targetChainId = Number(getEnv('REACT_APP_CHAIN_ID') || getEnv('VITE_CHAIN_ID') || 8453),
  chainConfig,
}) => {
  const [provider, setProvider]     = useState(null);
  const [signer, setSigner]         = useState(null);
  const [address, setAddress]       = useState(null);
  const [balance, setBalance]       = useState({ eth: '0', cpt: '0' });
  const [chainId, setChainId]       = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected]   = useState(false);
  const [error, setError]           = useState(null);
  const [networkName, setNetworkName] = useState('');
  const [txPending, setTxPending]   = useState(false);

  // Brief shimmer while first balances are loading after connect / switch
  const [isBalanceGlimmer, setIsBalanceGlimmer] = useState(false);

  // Modal state
  const [isModalOpen, setModalOpen] = useState(false);

  // Preferred provider (selected from modal)
  const preferredProviderRef = useRef(null);

  const safeTargetChainId = Number(targetChainId);
  const safeChainCfg = useMemo(
    () => resolveChainConfig(safeTargetChainId, chainConfig),
    [safeTargetChainId, chainConfig]
  );

  // Readonly RPC & rotation
  const roIndexRef = useRef(0);
  const readonlyProviderRef = useRef(null);
  const getRpcList = useCallback((cid) => {
    const id = Number(cid || safeTargetChainId);
    const chainCfg = getChainById(id);
    const primary = chainCfg?.rpcUrl?.() || '';
    const fallbacks = DEFAULT_RPC_FALLBACKS[id] || [];
    const combined = [primary, ...fallbacks].filter(Boolean);
    return combined.length ? combined : ['https://mainnet.base.org'];
  }, [safeTargetChainId]);

  const getReadonlyProvider = useCallback((cid) => {
    const list = getRpcList(cid);
    const idx = roIndexRef.current % list.length;
    const url = list[idx];
    const prov = new ethers.JsonRpcProvider(url);
    readonlyProviderRef.current = prov;
    return prov;
  }, [getRpcList]);

  const rotateReadonly = useCallback(() => {
    roIndexRef.current += 1;
    readonlyProviderRef.current = null;
  }, []);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // EIP-6963 registry wire-up
  useEffect(() => {
    try {
      window.__eip6963providers = window.__eip6963providers || [];
      const onAnnounce = (event) => {
        const detail = event?.detail; if (!detail || !detail.provider) return;
        const exists = window.__eip6963providers.some((x) => x.info?.uuid === detail.info?.uuid);
        if (!exists) window.__eip6963providers.push(detail);
      };
      window.addEventListener('eip6963:announceProvider', onAnnounce);
      window.dispatchEvent(new Event('eip6963:requestProvider'));
      return () => window.removeEventListener('eip6963:announceProvider', onAnnounce);
    } catch {}
  }, []);

  const handleAccountsChangedRef = useRef(null);
  const handleChainChangedRef    = useRef(null);
  const handleDisconnectRef      = useRef(null);
  const balanceIntervalRef       = useRef(null);
  const lastToastRef             = useRef(0);
  const lastBalanceUpdateRef     = useRef(0);
  const subscribedProviderRef    = useRef(null);
  const blockListenerFnRef       = useRef(null);
  const isDocVisibleRef          = useRef(true);
  const connectLockRef           = useRef(false);
  const txLockRef                = useRef(false);
  // Guard to avoid spamming CPT retry when an endpoint returns sticky 0
  const cptRetryKeyRef = useRef('');

  const dataLayerPush = useCallback((event, payload) => {
    try { if (typeof window !== 'undefined' && window.dataLayer) window.dataLayer.push({ event, ...payload }); } catch {}
  }, []);

  const dispatchAppEvent = useCallback((name, detail) => {
    try {
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent(name, { detail }));
      }
      dataLayerPush(name, detail);
    } catch {}
  }, [dataLayerPush]);

  const safeToast = useCallback((fn, msg, opts = {}) => {
    try {
      if (!mountedRef.current) return null;
      const now = Date.now();
      if (now - lastToastRef.current < 700) return null;
      lastToastRef.current = now;
      return fn(msg, {
        style: { background: theme.colors.card, color: theme.colors.text },
        progressStyle: { background: theme.colors.primary },
        ...opts,
      });
    } catch { return null; }
  }, []);

  /* ------------------------ Provider selection --------------------- */
  const LAST_WALLET_KEY = 'cp:lastWallet';
  const saveLastWallet = (prov) => {
    try { localStorage.setItem(LAST_WALLET_KEY, idForProvider(prov)); } catch {}
  };
  const readLastWalletId = () => {
    try { return localStorage.getItem(LAST_WALLET_KEY) || ''; } catch { return ''; }
  };

  const pickInjectedProvider = useCallback(() => {
    if (preferredProviderRef.current) return preferredProviderRef.current;
    const list = collectInjectedProviders();
    const sorted = sortProviders(list);
    // If we have a remembered id, prefer it
    const remembered = readLastWalletId();
    if (remembered && sorted.length) {
      const match = sorted.find((p) => idForProvider(p) === remembered);
      if (match) return match;
    }
    return sorted[0] || null;
  }, []);

  const rebuildProvider = useCallback(async (override) => {
    const injected = override || pickInjectedProvider();
    if (!injected) return { prov: null, sign: null, cid: null };
    const prov = new ethers.BrowserProvider(injected);
    const net  = await prov.getNetwork();
    const cid  = parseChainId(net.chainId);
    const sign = await prov.getSigner().catch(() => null);
    return { prov, sign, cid };
  }, [pickInjectedProvider]);

  /* ------------------------ Network enforcement -------------------- */
  const ensureCorrectChain = useCallback(async (override) => {
    const injected = override || pickInjectedProvider();
    if (!injected) {
      setError('No Ethereum wallet detected.');
      safeToast(toast.error, 'Please install MetaMask or Coinbase Wallet.');
      dispatchAppEvent('wallet_error', { reason: 'no_provider' });
      return { switched: false };
    }

    // Accept both Base and Camp as valid app networks
    const ALLOWED_CHAIN_IDS = new Set([8453, 484]);

    let currentChainId = null;
    try {
      const { cid } = await rebuildProvider(injected);
      currentChainId = cid;
    } catch {}

    if (ALLOWED_CHAIN_IDS.has(currentChainId)) {
      setSavedChainId(currentChainId);
      return { switched: false };
    }

    try {
      await injected.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: safeChainCfg.chainId }],
      });
    } catch (switchError) {
      const code = switchError?.code;
      const msg  = String(switchError?.message || '');
      const needsAdd = code === 4902 || code === -32603 || msg.toLowerCase().includes('unrecognized');
      if (needsAdd) {
        try {
          await injected.request({ method: 'wallet_addEthereumChain', params: [safeChainCfg] });
          await injected.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: safeChainCfg.chainId }] });
        } catch (addError) {
          setError(addError?.message || 'Failed to add network');
          safeToast(toast.error, 'Failed to add/switch network.');
          dispatchAppEvent('wallet_network_state', { ok: false, reason: 'add_failed' });
          return { switched: false };
        }
      } else if (code === -32002) {
        safeToast(toast.info, 'A network switch is already pending in your wallet.');
        return { switched: false };
      } else {
        setError(switchError?.message || 'Failed to switch network');
        safeToast(toast.error, 'Failed to switch network.');
        dispatchAppEvent('wallet_network_state', { ok: false, reason: 'switch_failed' });
        return { switched: false };
      }
    }

    await new Promise((resolve) => {
      let done = false;
      const handler = () => {
        if (done) return;
        done = true;
        try { injected.removeListener?.('chainChanged', handler); } catch {}
        resolve();
      };
      injected.on?.('chainChanged', handler);
      setTimeout(handler, 1200);
    });

    const { prov, sign, cid } = await rebuildProvider(injected);
    setProvider(prov);
    setSigner(sign);
    setChainId(cid);
    setNetworkName(resolveChainConfig(cid, chainConfig)?.chainName || '');
    setSavedChainId(cid);
    // Ensure first post-switch read uses a fresh RPC for the new chain
    readonlyProviderRef.current = null;
    roIndexRef.current = 0;
    dispatchAppEvent('wallet_network_state', { ok: true, chainId: cid });
    safeToast(toast.success, `Switched to ${resolveChainConfig(cid, chainConfig)?.chainName || 'network'}`, {
      style: { background: theme.colors.card, color: theme.colors.success },
    });
    // Optional safety: reset readonly RPC after ensureCorrectChain
    readonlyProviderRef.current = null;
    roIndexRef.current = 0;
    return { switched: true };
  }, [safeChainCfg, rebuildProvider, safeToast, dispatchAppEvent, pickInjectedProvider, chainConfig]);

  /* ------------------------ Balances (readonly RPC) ---------------- */
  const readERC20Balance = useCallback(
    async (provRead, account, cid) => {
      try {
        const token = resolveTokenAddress(cid);
        if (!token || !ethers.isAddress(token)) return '0';
        const code = await provRead.getCode(token);
        if (!code || code === '0x') return '0';
        const erc20 = new ethers.Contract(token, ChainPlayTokenABI, provRead);
        const bal = await erc20.balanceOf(account);
        return ethers.formatEther(bal);
      } catch (err) {
        // Some RPCs or wrong-chain calls return CALL_EXCEPTION / empty revert data.
        // Treat as zero balance rather than surfacing an error.
        if (
          isBadDataDecode(err) ||
          String(err?.code || '').toUpperCase() === 'CALL_EXCEPTION'
        )
          return '0';
        try {
          // Extra safety: if the address has no code, it's not a contract → zero balance.
          const token = resolveTokenAddress(cid);
          const hasCode = token && (await provRead.getCode(token).catch(() => null));
          if (!hasCode || hasCode === '0x') return '0';
        } catch {}
        throw err;
      }
    },
    []
  );

  const updateBalances = useCallback(
    async (acc, _prov, cid) => {
      if (!acc) return;
      let provRead = readonlyProviderRef.current || getReadonlyProvider(cid ?? chainId);
      try {
        const [ethBal, cptBal] = await Promise.all([
          provRead.getBalance(acc),
          readERC20Balance(provRead, acc, cid ?? chainId),
        ]);
        const ethStr = ethers.formatEther(ethBal);
        const cptStr = cptBal;
        setBalance((prev) => (!prev || prev.eth !== ethStr || prev.cpt !== cptStr) ? { eth: ethStr, cpt: cptStr } : prev);
        // If CPT read returned 0, schedule one guarded retry on a rotated RPC to avoid sticky zero from a flaky endpoint
        try {
          const keyCid = cid ?? chainId;
          const tok = resolveTokenAddress(keyCid);
          const retryKey = `${acc || '0x'}:${keyCid}`;
          if (tok && acc && cptStr === '0' && cptRetryKeyRef.current !== retryKey) {
            cptRetryKeyRef.current = retryKey;
            setTimeout(() => {
              if (!mountedRef.current) return;
              if (address === acc) {
                rotateReadonly();
                const provRetry = readonlyProviderRef.current || getReadonlyProvider(keyCid);
                updateBalances(acc, provRetry, cid);
              }
              // allow future retries if user disconnects/reconnects or address changes
              // we do not clear here to prevent tight loops; disconnect clears it
            }, 1200);
          }
        } catch {}
      } catch (err) {
        // Try rotating RPC once
        rotateReadonly();
        provRead = getReadonlyProvider(cid ?? chainId);
        try {
          const [ethBal, cptBal] = await Promise.all([
            provRead.getBalance(acc),
            readERC20Balance(provRead, acc, cid ?? chainId),
          ]);
          setBalance({ eth: ethers.formatEther(ethBal), cpt: cptBal });
        } catch (err2) {
          const msg = err2?.message || 'Failed to update balances';
          setError(msg);
          safeToast(toast.error, `Failed to update balances: ${msg}`, {
            style: { background: theme.colors.card, color: theme.colors.danger },
          });
        }
      }
    },
    [readERC20Balance, chainId, getReadonlyProvider, rotateReadonly, safeToast, address]
  );

  // Visibility throttle
  useEffect(() => {
    const onVis = () => {
      try {
        const vis = document.visibilityState !== 'hidden';
        isDocVisibleRef.current = vis;
        if (vis && address) {
          lastBalanceUpdateRef.current = 0;
          updateBalances(address, provider, chainId);
        } else {
          // When hidden, unhook block listeners to save CPU
          try {
            if (subscribedProviderRef.current && blockListenerFnRef.current) {
              subscribedProviderRef.current.off?.('block', blockListenerFnRef.current);
            }
          } catch {}
        }
      } catch {}
    };
    try { isDocVisibleRef.current = document.visibilityState !== 'hidden'; } catch {}
    window.addEventListener('visibilitychange', onVis);
    return () => window.removeEventListener('visibilitychange', onVis);
  }, [address, provider, chainId, updateBalances]);

  const startBalancePolling = useCallback((acc, _prov, cid) => {
    if (!acc) return;

    try {
      if (subscribedProviderRef.current && blockListenerFnRef.current) {
        subscribedProviderRef.current.off?.('block', blockListenerFnRef.current);
      }
    } catch {}

    const provRead = readonlyProviderRef.current || getReadonlyProvider(cid ?? chainId);
    subscribedProviderRef.current = provRead;

    const onBlock = async () => {
      if (!isDocVisibleRef.current) return;
      const now = Date.now();
      if (now - lastBalanceUpdateRef.current < 1200) return;
      lastBalanceUpdateRef.current = now;
      await updateBalances(acc, provRead, cid);
    };
    blockListenerFnRef.current = onBlock;

    try {
      provRead.on?.('block', onBlock);
    } catch {
      if (balanceIntervalRef.current) clearInterval(balanceIntervalRef.current);
      balanceIntervalRef.current = setInterval(
        () => updateBalances(acc, provRead, cid),
        10000
      );
    }
  }, [updateBalances, chainId, getReadonlyProvider]);

  const stopBalancePolling = useCallback(() => {
    try {
      if (subscribedProviderRef.current && blockListenerFnRef.current) {
        subscribedProviderRef.current.off?.('block', blockListenerFnRef.current);
      }
    } catch {}
    subscribedProviderRef.current = null;
    blockListenerFnRef.current = null;
    if (balanceIntervalRef.current) {
      clearInterval(balanceIntervalRef.current);
      balanceIntervalRef.current = null;
    }
  }, []);

  /* ------------------------ Connect / Disconnect -------------------- */

  const watchCPTAsset = useCallback(async () => {
    try {
      const chosen = preferredProviderRef.current || pickInjectedProvider();
      if (!chosen || !address) return false;
      const cid   = chainId ?? safeTargetChainId;
      const token = resolveTokenAddress(cid);
      if (!token || !ethers.isAddress(token)) return false;
      await chosen.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: token,
            symbol: getEnv('REACT_APP_TOKEN_SYMBOL', 'CPT'),
            decimals: Number(getEnv('REACT_APP_TOKEN_DECIMALS', '18')),
            image: getEnv('REACT_APP_TOKEN_ICON', ''),
          },
        },
      });
      return true;
    } catch {
      return false;
    }
  }, [address, chainId, safeTargetChainId, pickInjectedProvider]);
  const connectWallet = useCallback(async (overrideProvider) => {
    const chosen = overrideProvider || pickInjectedProvider();
    if (!chosen) {
      if (isMobileUA()) setModalOpen(true);
      else {
        setError('No Ethereum wallet detected.');
        safeToast(toast.error, 'Install MetaMask or Coinbase Wallet to continue.');
      }
      return;
    }
    if (connectLockRef.current) return;
    connectLockRef.current = true;
    setIsConnecting(true);
    setError(null);
    try {
      // Stabilize provider reference before requesting accounts
      preferredProviderRef.current = chosen;
      const accounts = await chosen.request({ method: 'eth_requestAccounts' });
      if (!accounts || accounts.length === 0) throw new Error('No accounts returned');

      // Ensure network first
      await ensureCorrectChain(chosen);
      // Reset readonly RPC so first post-connect read uses a fresh endpoint
      readonlyProviderRef.current = null;
      roIndexRef.current = 0;

      const { prov, sign, cid } = await rebuildProvider(chosen);
      if (!prov) throw new Error('Provider not available');

      const acc = accounts[0];
      setProvider(prov);
      setSigner(sign);
      setAddress(acc);
      setChainId(cid);
      setIsConnected(true);
      setNetworkName(resolveChainConfig(cid, chainConfig)?.chainName || '');
      setSavedChainId(cid);

      // Show brief glimmer while balances populate
      setIsBalanceGlimmer(true);
      saveLastWallet(chosen);

      await updateBalances(acc, prov, cid);
      setTimeout(() => setIsBalanceGlimmer(false), 900);
      startBalancePolling(acc, prov, cid);

      // Listeners on chosen provider
      chosen.on?.('accountsChanged', handleAccountsChangedRef.current);
      chosen.on?.('chainChanged', handleChainChangedRef.current);
      chosen.on?.('disconnect', handleDisconnectRef.current);

      safeToast(toast.success, 'Wallet connected', {
        style: { background: theme.colors.card, color: theme.colors.success },
      });
      setModalOpen(false);
      dispatchAppEvent('wallet_connected', { address: acc, chainId: cid });

      // Optional one-time prompt to add CPT
      try {
        const k = 'cp:tokenPrompted';
        if (!localStorage.getItem(k)) {
          localStorage.setItem(k, '1');
          setTimeout(() => { watchCPTAsset().catch(() => {}); }, 250);
        }
      } catch {}
    } catch (err) {
      const msg = err?.message || 'Connection failed';
      setError(msg);
      if (err?.code === 4001) {
        safeToast(toast.info, 'Connection request cancelled.');
      } else if (err?.code === -32002) {
        safeToast(toast.info, 'A connection request is already pending in your wallet.');
      } else {
        safeToast(toast.error, `Connection failed: ${msg}`, {
          style: { background: theme.colors.card, color: theme.colors.danger },
        });
      }
      dispatchAppEvent('wallet_error', { reason: 'connect_failed', message: msg });
    } finally {
      connectLockRef.current = false;
      setIsConnecting(false);
    }
  }, [
    ensureCorrectChain,
    rebuildProvider,
    chainConfig,
    updateBalances,
    startBalancePolling,
    dispatchAppEvent,
    safeToast,
    pickInjectedProvider,
    watchCPTAsset,
  ]);

  const disconnectWallet = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAddress(null);
    setBalance({ eth: '0', cpt: '0' });
    setChainId(null);
    setIsConnected(false);
    setError(null);
    setNetworkName('');
    setTxPending(false);
    setIsBalanceGlimmer(false);
    stopBalancePolling();

    const chosen = preferredProviderRef.current || null;
    if (chosen) {
      chosen.removeListener?.('accountsChanged', handleAccountsChangedRef.current);
      chosen.removeListener?.('chainChanged', handleChainChangedRef.current);
      chosen.removeListener?.('disconnect', handleDisconnectRef.current);
    }
    // Reset provider refs so a fresh connect re-initializes cleanly
    preferredProviderRef.current = null;
    readonlyProviderRef.current = null;
    roIndexRef.current = 0;
    cptRetryKeyRef.current = '';

    safeToast(toast.info, 'Wallet disconnected');
    dispatchAppEvent('wallet_disconnected', {});
  }, [stopBalancePolling, dispatchAppEvent, safeToast]);

  /* ------------------------ Lifecycle listeners --------------------- */
  useEffect(() => {
    handleAccountsChangedRef.current = async (accounts) => {
      if (!accounts || accounts.length === 0) {
        disconnectWallet();
      } else {
        const acc = accounts[0];
        setAddress(acc);
        const chosen = preferredProviderRef.current || null;
        await ensureCorrectChain(chosen || undefined);
        const { prov, cid } = await rebuildProvider(chosen || undefined);
        await updateBalances(acc, prov, cid);
        startBalancePolling(acc, prov, cid);
      }
    };
  }, [disconnectWallet, ensureCorrectChain, rebuildProvider, updateBalances, startBalancePolling]);

  useEffect(() => {
    handleChainChangedRef.current = async (newChainId) => {
      const id = parseChainId(newChainId);
      setChainId(id);
      setNetworkName(resolveChainConfig(id, chainConfig)?.chainName || '');
      setSavedChainId(id);

      // Accept both Base and Camp; only enforce switch on unsupported networks
      const ALLOWED_CHAIN_IDS = new Set([8453, 484]);
      if (!ALLOWED_CHAIN_IDS.has(id)) {
        setError('Wrong network detected');
        safeToast(toast.warn, `Switching to ${resolveChainConfig(safeTargetChainId, chainConfig).chainName}…`);
        const chosen = preferredProviderRef.current || undefined;
        await ensureCorrectChain(chosen);
        return;
      }

      setError(null);
      dispatchAppEvent('wallet_network_state', { ok: true, chainId: id });
      // Refresh readonly RPC to avoid stale reads after a chain change
      readonlyProviderRef.current = null;
      roIndexRef.current = 0;
      const { prov } = await rebuildProvider(preferredProviderRef.current || undefined);
      setIsBalanceGlimmer(true);
      await updateBalances(address, prov, id);
      startBalancePolling(address, prov, id);
      setTimeout(() => setIsBalanceGlimmer(false), 900);
    };
  }, [
    safeTargetChainId,
    chainConfig,
    ensureCorrectChain,
    rebuildProvider,
    updateBalances,
    address,
    startBalancePolling,
    dispatchAppEvent,
    safeToast,
  ]);

  useEffect(() => {
    handleDisconnectRef.current = () => {
      disconnectWallet();
    };
  }, [disconnectWallet]);

  useEffect(() => {
    const init = async () => {
      if (!autoConnect) return;
      const list = collectInjectedProviders();
      if (!list.length) return;
      try {
        const remembered = readLastWalletId();
        const sorted = sortProviders(list);
        preferredProviderRef.current =
          (remembered && sorted.find(p => idForProvider(p) === remembered)) || sorted[0] || null;

        const accounts = await preferredProviderRef.current.request({ method: 'eth_accounts' });
        if (accounts && accounts.length > 0) {
          await ensureCorrectChain(preferredProviderRef.current);
          const { prov, sign, cid } = await rebuildProvider(preferredProviderRef.current);
          const acc = accounts[0];
          setProvider(prov);
          setSigner(sign);
          setAddress(acc);
          setChainId(cid);
          setIsConnected(true);
          setNetworkName(resolveChainConfig(cid, chainConfig)?.chainName || '');
          setSavedChainId(cid);
          setIsBalanceGlimmer(true);
          await updateBalances(acc, prov, cid);
          setTimeout(() => setIsBalanceGlimmer(false), 900);
          startBalancePolling(acc, prov, cid);

          preferredProviderRef.current.on?.('accountsChanged', handleAccountsChangedRef.current);
          preferredProviderRef.current.on?.('chainChanged', handleChainChangedRef.current);
          preferredProviderRef.current.on?.('disconnect', handleDisconnectRef.current);

          dispatchAppEvent('wallet_connected', { address: acc, chainId: cid, silent: true });
        }
      } catch (err) {
        dispatchAppEvent('wallet_error', { reason: 'auto_connect_failed', message: err?.message });
      }
    };
    init();
    return () => {
      stopBalancePolling();
      const chosen = preferredProviderRef.current || null;
      if (chosen) {
        chosen.removeListener?.('accountsChanged', handleAccountsChangedRef.current);
        chosen.removeListener?.('chainChanged', handleChainChangedRef.current);
        chosen.removeListener?.('disconnect', handleDisconnectRef.current);
      }
    };
  }, [
    autoConnect,
    ensureCorrectChain,
    rebuildProvider,
    chainConfig,
    updateBalances,
    startBalancePolling,
    dispatchAppEvent,
    stopBalancePolling,
  ]);

  useEffect(() => {
    const listener = () => setModalOpen(true);
    window.addEventListener('open_wallet_connect', listener);
    return () => window.removeEventListener('open_wallet_connect', listener);
  }, []);

  useEffect(() => {
    if (address && provider) updateBalances(address, provider, chainId);
  }, [address, provider, chainId, updateBalances]);

  /* ------------------------ Contract / TX helpers ------------------- */
  const getContract = useCallback(
    (contractAddress, abi) => {
      if (!signer) throw new Error('Signer not available');
      return new ethers.Contract(contractAddress, abi, signer);
    },
    [signer]
  );

  const TX_CONFIRMATIONS = Number(getEnv('REACT_APP_TX_CONFIRMATIONS', '1'));
  const explorerTxUrl = useCallback((cid, hash) => {
    const exps = resolveChainConfig(cid, chainConfig)?.blockExplorerUrls || [];
    const base =
      exps[0] ||
      (cid === 8453 ? 'https://basescan.org' : '');
    return base ? `${base}/tx/${hash}` : '';
  }, [chainConfig]);

  const sendTransaction = useCallback(
    async (tx) => {
      if (!signer) throw new Error('Signer not available');
      if (txLockRef.current) {
        safeToast(toast.info, 'A transaction is already pending. Finish or cancel it in your wallet.');
        return null;
      }
      txLockRef.current = true;
      setTxPending(true);
      try {
        // Preflight: estimateGas + call
        try {
          await signer.estimateGas(tx);
          await signer.provider.call(tx);
        } catch (preErr) {
          const msg = preErr?.reason || preErr?.message || 'Transaction preflight failed';
          safeToast(toast.error, msg, { autoClose: 5000 });
          dispatchAppEvent('wallet_error', { reason: 'tx_preflight_failed', message: msg });
          throw preErr;
        }

        const toastId = toast.loading('Submitting transaction…', {
          style: { background: theme.colors.card, color: theme.colors.text },
        });

        const transaction = await signer.sendTransaction(tx);
        toast.update(toastId, { render: 'Pending confirmation…', isLoading: true });

        await transaction.wait(TX_CONFIRMATIONS);
        toast.update(toastId, {
          render: (
            <span>
              Confirmed{TX_CONFIRMATIONS > 1 ? ` (${TX_CONFIRMATIONS}/${TX_CONFIRMATIONS})` : ''}.{' '}
              {explorerTxUrl(chainId ?? safeTargetChainId, transaction.hash) ? (
                <a
                  href={explorerTxUrl(chainId ?? safeTargetChainId, transaction.hash)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: theme.colors.primary, textDecoration: 'underline' }}
                >
                  View on explorer
                </a>
              ) : null}
            </span>
          ),
          isLoading: false,
          autoClose: 4000,
        });

        dispatchAppEvent('wallet_tx_confirmed', { hash: transaction.hash, chainId: chainId ?? safeTargetChainId });
        return transaction;
      } catch (err) {
        const msg = err?.message || 'Transaction failed';
        setError(msg);
        if (err?.code === 4001) {
          safeToast(toast.info, 'Transaction cancelled.');
        } else if (String(msg).includes('already known')) {
          safeToast(toast.info, 'This transaction was already submitted.');
        } else {
          safeToast(toast.error, `Transaction failed: ${msg}`, {
            style: { background: theme.colors.card, color: theme.colors.danger },
          });
        }
        dispatchAppEvent('wallet_error', { reason: 'tx_failed', message: msg });
        throw err;
      } finally {
        txLockRef.current = false;
        setTxPending(false);
      }
    },
    [signer, chainId, safeTargetChainId, TX_CONFIRMATIONS, explorerTxUrl, dispatchAppEvent, safeToast]
  );


  /* ------------------------ Context value --------------------------- */
  const openConnectModal  = useCallback(() => setModalOpen(true), []);
  const closeConnectModal = useCallback(() => setModalOpen(false), []);

  const value = useMemo(
    () => ({
      // Actions
      connectWallet,
      disconnectWallet,
      switchChain: ensureCorrectChain,
      updateBalances,
      getContract,
      sendTransaction,
      watchCPTAsset,

      // Modal controls
      openConnectModal,
      closeConnectModal,

      // Legacy aliases
      connect: connectWallet,
      openConnect: openConnectModal,
      open: openConnectModal,
      requestConnect: openConnectModal,
      promptConnect: openConnectModal,

      // State
      address,
      balance,
      signer,
      provider,
      chainId,
      networkName,
      isConnecting,
      isConnected,
      txPending,
      error,
      isBalanceGlimmer,
    }),
    [
      connectWallet,
      disconnectWallet,
      ensureCorrectChain,
      updateBalances,
      getContract,
      sendTransaction,
      watchCPTAsset,
      openConnectModal,
      closeConnectModal,
      address,
      balance,
      signer,
      provider,
      chainId,
      networkName,
      isConnecting,
      isConnected,
      txPending,
      error,
      isBalanceGlimmer,
    ]
  );

  return (
    <WalletContext.Provider value={value}>
      {/* ARIA live region for subtle state changes */}
      <Box sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} role="status" aria-live="polite">
        {isConnected ? 'Wallet connected' : 'Wallet disconnected'}
      </Box>

      {children}

      <WalletModalInternal
        open={isModalOpen}
        onClose={closeConnectModal}
        onPick={(p) => {
          preferredProviderRef.current = p;
          saveLastWallet(p);
          connectWallet(p);
        }}
        chainName={networkName || resolveChainConfig(safeTargetChainId, chainConfig)?.chainName}
      />
    </WalletContext.Provider>
  );
};

WalletProvider.defaultProps = { autoConnect: false, targetChainId: 8453, chainConfig: null };

/* ================================================================== */
/* Wallet selection modal                                              */
/* ================================================================== */
const WalletRow = ({ label, sublabel, onClick, href, disabled, adornment }) => {
  const Content = (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' ? onClick?.(e) : null)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: 1.25,
        border: `1px solid ${COLOR_BORDER}`,
        bgcolor: 'var(--color-surface, rgba(0,0,0,0.32))',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        outline: 'none',
        transition: 'filter .18s ease, border-color .18s ease',
        '&:hover': { filter: disabled ? 'none' : 'brightness(1.08)' },
        '&:focus-visible': { outline: `2px solid ${COLOR_FOCUS}`, outlineOffset: 2 },
      }}
      aria-disabled={disabled ? 'true' : 'false'}
    >
      <Box sx={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', flex: '0 0 28px', bgcolor: 'rgba(255,255,255,0.08)' }}>
        {adornment || null}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: COLOR_TEXT, lineHeight: 1.1 }}>
          {label}
        </Typography>
        {sublabel ? (
          <Typography variant="caption" sx={{ color: COLOR_SUBTEXT }}>
            {sublabel}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ fontSize: 12, color: COLOR_SUBTEXT, whiteSpace: 'nowrap' }}>Select</Box>
    </Box>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        {Content}
      </a>
    );
  }
  return Content;
};

const WalletModalInternal = ({ open, onClose, onPick, chainName }) => {
  const fullScreen = useMediaQuery('(max-width:600px)');
  // Compute providers directly (no hooks needed, avoids lint noise)
  const providers = sortProviders(collectInjectedProviders());
  const prevFocusRef = useRef(null);

  // manage focus return
  useEffect(() => {
    if (open) {
      prevFocusRef.current = document.activeElement;
    } else if (prevFocusRef.current && typeof prevFocusRef.current.focus === 'function') {
      setTimeout(() => prevFocusRef.current?.focus(), 0);
    }
  }, [open]);

  const metaMask = providers.find(p => p?.isMetaMask) || null;
  const coinbase = providers.find(p => p?.isCoinbaseWallet) || null;
  const fallback = providers.find(p => !p?.isMetaMask && !p?.isCoinbaseWallet) || null;

  const mobile = isMobileUA();

  // Mobile deep links when nothing injected
  const mmLink  = deepLinkFor('metamask');
  const cbLink  = deepLinkFor('coinbase');

  const handlePick = (prov) => {
    if (!prov) return;
    onPick?.(prov);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="wallet-modal-title"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          border: `1px solid ${COLOR_BORDER}`,
          bgcolor: 'var(--color-card, rgba(10,12,16,0.78))',
          backdropFilter: 'blur(14px)',
          maxWidth: 420,
          width: '100%',
        }
      }}
    >
      <DialogTitle
        id="wallet-modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, gap: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: COLOR_TEXT }}>
            Choose a Wallet
          </Typography>
          <Typography variant="caption" sx={{ color: COLOR_SUBTEXT, display: 'block' }}>
            Connect to continue on Base
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {chainName ? (
            <Chip
              size="small"
              variant="outlined"
              label={chainName}
              sx={{
                borderColor: 'rgba(255,255,255,0.18)',
                color: COLOR_SUBTEXT,
                height: 24,
              }}
            />
          ) : null}
          <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: COLOR_SUBTEXT }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        {/* In-app browser notice */}
        {isInAppBrowser() && (
          <Box
            sx={{
              mb: 1.5,
              p: 1,
              borderRadius: 1,
              border: `1px dashed ${COLOR_BORDER}`,
              color: COLOR_SUBTEXT,
              fontSize: 12,
            }}
          >
            You appear to be in an in‑app browser. If you don’t see your wallet,
            open this page in Safari/Chrome or use the buttons below to open a wallet app.
          </Box>
        )}

        {/* Wallet list */}
        <Stack spacing={1.1}>
          {/* MetaMask */}
          {metaMask ? (
            <WalletRow
              label="MetaMask"
              sublabel="Browser wallet"
              onClick={() => handlePick(metaMask)}
            />
          ) : (
            <WalletRow
              label="MetaMask"
              sublabel={mobile ? 'Open in MetaMask app' : 'Install extension'}
              href={mobile ? mmLink : 'https://metamask.io/download/'}
            />
          )}

          {/* Coinbase Wallet */}
          {coinbase ? (
            <WalletRow
              label="Coinbase Wallet"
              sublabel="Browser wallet"
              onClick={() => handlePick(coinbase)}
            />
          ) : (
            <WalletRow
              label="Coinbase Wallet"
              sublabel={mobile ? 'Open in Coinbase Wallet' : 'Install extension'}
              href={mobile ? cbLink : 'https://www.coinbase.com/wallet/downloads'}
            />
          )}

          {/* Other injected */}
          {fallback ? (
            <WalletRow
              label={labelForProvider(fallback)}
              sublabel="Detected"
              onClick={() => handlePick(fallback)}
            />
          ) : providers.length ? (
            providers.map((p, i) => {
              if (p === metaMask || p === coinbase) return null;
              return (
                <WalletRow
                  key={i}
                  label={labelForProvider(p)}
                  sublabel="Detected"
                  onClick={() => handlePick(p)}
                />
              );
            })
          ) : null}

          {/* WalletConnect v2 (QR) hook — disabled by default to avoid bundler dep.
              To enable: npm i @walletconnect/ethereum-provider
              Then replace onClick with lazy import & init of WC provider, pass to onPick(). */}
          {/* <WalletRow
            label="WalletConnect (QR)"
            sublabel="Scan with any compatible wallet"
            onClick={async () => {
              toast.info('Enable WalletConnect by installing @walletconnect/ethereum-provider', {
                style: { background: theme.colors.card, color: theme.colors.text },
              });
            }}
          /> */}
        </Stack>

        {/* Divider */}
        <Divider sx={{ my: 1.75, borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* Tips */}
        <Stack spacing={1}>
          <Typography variant="caption" sx={{ color: COLOR_SUBTEXT }}>
            • We’ll auto‑switch to Base and rebuild your session for safety.
          </Typography>
          <Typography variant="caption" sx={{ color: COLOR_SUBTEXT }}>
            • Long‑press your address later to copy it; right‑click wallet button to add CPT token.
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

/* ================================================================== */
/* Wallet Button                                                       */
/* ================================================================== */
const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');

export const WalletButton = () => {
  const {
    openConnectModal,
    disconnectWallet,
    address,
    isConnecting,
    txPending,
    error,
    chainId,
    watchCPTAsset,
    isBalanceGlimmer,
  } = useContext(WalletContext);

  const label = isConnecting ? '' : txPending ? 'Pending…' : address ? 'Disconnect' : 'Connect';
  const disabled = isConnecting || txPending;

  // Long-press copy / context add-asset
  const pressTimerRef = useRef(null);
  const clearPress = () => { if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; } };
  const copyAddr = async () => {
    try {
      if (!address) return;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(address);
        toast.success('Address copied', { style: { background: theme.colors.card, color: theme.colors.success } });
      }
    } catch {}
  };
  const onMouseDown   = () => { if (!address) return; clearPress(); pressTimerRef.current = setTimeout(copyAddr, 600); };
  const onMouseUp     = () => clearPress();
  const onMouseLeave  = () => clearPress();
  const onKeyDown     = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') { e.preventDefault(); copyAddr(); } };
  const onContextMenu = async (e) => {
    if (!address) return;
    try {
      e.preventDefault();
      await watchCPTAsset();
      toast.info('Attempting to add CPT token', { style: { background: theme.colors.card, color: theme.colors.text } });
    } catch {}
  };

  return (
    <Box sx={{ position: 'relative', px: { xs: 0.5, md: 1 } }}>
      <Tooltip
        title={
          error
            ? error
            : address
            ? `${shortAddr(address)}${chainId ? ` • ${chainId}` : ''} — long‑press to copy, right‑click to add token`
            : 'Connect Wallet'
        }
        arrow
      >
        <motion.div whileHover={{ scale: disabled ? 1.0 : 1.05 }} whileTap={{ scale: disabled ? 1.0 : 0.95 }}>
          <Button
            onClick={address ? disconnectWallet : openConnectModal}
            disabled={disabled}
            data-connect-wallet
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            onKeyDown={onKeyDown}
            onContextMenu={onContextMenu}
            sx={{
              position: 'relative',
              borderRadius: RADIUS_BUTTON,
              px: { xs: 1.25, md: 1.5 },
              py: { xs: 0.6, md: 0.7 },
              fontSize: { xs: 12, md: 13 },
              fontWeight: 900,
              minWidth: { xs: 96, md: 104 },
              minHeight: { xs: 32, md: 36 },
              height: { xs: 32, md: 36 },
              lineHeight: 1,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              transition: 'all .18s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundImage: 'var(--fx-button-glint-url)',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                opacity: 'var(--fx-button-glint-opacity)',
                transition: 'opacity .18s ease',
                pointerEvents: 'none',
              },
              '&:hover::before': { opacity: 'var(--fx-button-glint-hover)' },
              '&:focus-visible': {
                outline: `2px solid ${COLOR_FOCUS}`,
                outlineOffset: 2,
                borderRadius: RADIUS_BUTTON,
              },
              ...(address
                ? {
                    bgcolor: 'var(--color-surfaceAlt)',
                    color: COLOR_TEXT,
                    border: `1px solid ${COLOR_BORDER}`,
                    boxShadow: 'none',
                    '&:hover': { borderColor: COLOR_PRIMARY, bgcolor: 'rgba(0,245,255,0.10)' },
                  }
                : {
                    backgroundImage: GRAD_PRIMARY,
                    bgcolor: 'transparent',
                    color: BUTTON_LABEL_PRIMARY,
                    border: '1px solid transparent',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.32)',
                    '&:hover': { filter: 'saturate(1.05)' },
                  }),
              '&:disabled': {
                bgcolor: 'rgba(255,255,255,0.08)',
                color: COLOR_SUBTEXT,
                border: '1px solid transparent',
                boxShadow: 'none',
              },
            }}
            aria-label={address ? 'Disconnect Wallet' : (txPending ? 'Transaction pending' : 'Open Connect Modal')}
            data-testid="wallet-button"
          >
            {isConnecting ? <CircularProgress size={12} sx={{ color: theme.colors.primary }} /> : label}
          </Button>
          {isBalanceGlimmer && (
            <Box sx={{ mt: 0.5, height: 3, borderRadius: 2, position: 'relative', overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.08)' }}>
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
                style={{ position: 'absolute', top: 0, bottom: 0, width: '45%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)' }}
              />
            </Box>
          )}
        </motion.div>
      </Tooltip>
    </Box>
  );
};
